/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */
import { jsx } from '@emotion/react'
import { useCallback, useState } from 'react'

import {
  EuiButtonIcon,
  EuiCode,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormHelpText,
  EuiPanel,
  EuiPopover,
  EuiSpacer,
  EuiText,
  EuiTextTruncate,
  EuiTitle,
  EuiToolTip,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import CopyButton from '@modules/cui/CopyButton'
import PrivacySensitiveContainer from '@modules/cui/PrivacySensitiveContainer'
import { DocLink } from '@modules/doclinks'

const EndpointsCloudPanelTooltip = () => {
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false)

  const openPopover = useCallback(() => {
    setIsPopoverOpen((prev) => !prev)
  }, [])

  const closePopover = useCallback(() => {
    setIsPopoverOpen(false)
  }, [])

  const button = (
    <EuiButtonIcon
      data-test-id='endpoints-cloud-panel.tooltip-button'
      onClick={openPopover}
      iconType='question'
      color='text'
      aria-label='Open Cloud ID tooltip'
    />
  )

  return (
    <EuiPopover
      id='cloud-id'
      ownFocus={true}
      button={button}
      panelStyle={{ width: '15rem' }}
      panelPaddingSize='m'
      anchorPosition='rightCenter'
      isOpen={isPopoverOpen}
      closePopover={closePopover}
    >
      <EuiFormHelpText data-test-id='endpoints-cloud-panel.tooltip-content'>
        <FormattedMessage
          id='endpoints-cloud-panel.cloud-id-description'
          defaultMessage='Get started with Beats and Logstash quickly. The Cloud ID simplifies sending data to your cluster on Elastic Cloud.'
        />
        {` `}
        <DocLink link='cloudIdDocLink'>
          <FormattedMessage
            id='endpoints-cloud-panel.cloud-id-description-link'
            defaultMessage='Learn more'
          />
        </DocLink>
      </EuiFormHelpText>
    </EuiPopover>
  )
}

interface EndpointsCloudPanelProps {
  cloudId: string
}

export const EndpointsCloudPanel = (props: EndpointsCloudPanelProps) => {
  const { cloudId } = props

  const [isTextHidden, setIsTextHidden] = useState<boolean>(true)

  const toggleHide = useCallback(() => {
    setIsTextHidden((isHidden) => !isHidden)
  }, [])

  const renderCloudId = () => {
    if (isTextHidden) {
      return (
        <EuiText size='s' color='subdued'>
          <EuiTextTruncate
            title='Cloud ID hidden'
            data-test-id='cloud-id-container'
            text={[...cloudId].map(() => '*').join('')}
            ellipsis=''
          />
        </EuiText>
      )
    }

    return (
      <EuiText size='s'>
        <EuiCode transparentBackground={true} style={{ padding: 0 }}>
          <EuiTextTruncate text={cloudId} data-test-id='cloud-id-container' />
        </EuiCode>
      </EuiText>
    )
  }

  const getToolTipMessage = () => {
    if (isTextHidden) {
      return <FormattedMessage id='endpoints-cloud-panel.unhide' defaultMessage='Unhide' />
    }

    return <FormattedMessage id='endpoints-cloud-panel.hide' defaultMessage='Hide' />
  }

  return (
    <EuiPanel hasBorder={true} data-test-id='endpoints-cloud-panel' grow={false}>
      <EuiFlexGroup direction='column' gutterSize='xs' style={{ height: '100%' }}>
        <EuiFlexItem grow={false}>
          <EuiFlexGroup direction='row' alignItems='center' gutterSize='xs'>
            <EuiFlexItem grow={false}>
              <EuiTitle size='xxs'>
                <h3>
                  <FormattedMessage id='endpoints-cloud-panel.title' defaultMessage='Cloud ID' />
                </h3>
              </EuiTitle>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EndpointsCloudPanelTooltip />
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>

        <EuiSpacer size='xs' />

        <EuiFlexItem>
          <EuiFlexGroup direction='row' alignItems='center'>
            {/* Cloud ID string */}
            <EuiFlexItem>
              <PrivacySensitiveContainer>{renderCloudId()}</PrivacySensitiveContainer>
            </EuiFlexItem>
            {/* Side buttons  */}
            <EuiFlexItem grow={false}>
              <EuiFlexGroup direction='row' gutterSize='s'>
                <EuiFlexItem grow={false}>
                  <EuiToolTip content={<EuiText>{getToolTipMessage()}</EuiText>}>
                    <EuiButtonIcon
                      aria-label={isTextHidden ? 'Unhide Cloud ID' : 'Hide Cloud ID'}
                      data-test-id='endpoints-cloud-panel.unhide-button'
                      iconType={isTextHidden ? 'eye' : 'eyeClosed'}
                      color='text'
                      display='base'
                      size='xs'
                      onClick={toggleHide}
                    />
                  </EuiToolTip>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <CopyButton
                    data-test-id='endpoints-cloud-panel.copy-button'
                    aria-label='Copy Cloud ID'
                    value={cloudId}
                    color='text'
                    display='base'
                    size='xs'
                  />
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>
        <EuiSpacer size='xs' />
      </EuiFlexGroup>
    </EuiPanel>
  )
}
